import React, { useEffect } from "react";
import Footer from "@/components/Layout/Footer";
import { usePrivy } from "@privy-io/react-auth";
import { Box, Fade } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import SpinnerLoading from "@/components/SpinnerLoading";
import Navigation from "@/components/Layout/Navbar";
import AutoLogout from "../AutoLogout";
import { setCookie } from "typescript-cookie";
import { AXIOS_INSTANCE } from "@/api/mutator/custom-instance";
import { useNavigationUtils } from "@/utils/navigationHooks";
import { useTransactionSnackbar } from "@/hooks/useTransactionSnackbar";
import config from "@/config"; // Import config to access PATHNAME object
import DetectNetwork from "./component/DetectNetwork";
import DetectDifferentAccount from "./component/DetectDifferentAccount";
import { signinRequest } from "@/api/auth";
import { axiosInstance } from "@/utils/axios";
import { useAuth } from "@/provider/AuthProvider";
import { QUERY_KEY } from "@/constants/query-key";
import { getCookie } from "typescript-cookie";

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { ready, authenticated, user } = usePrivy();
  const { isLoading } = useAuth();
  const queryClient = useQueryClient();
  const router = useRouter();
  const { handleLogout } = useNavigationUtils();
  const { showTransactionSnackbar } = useTransactionSnackbar();

  const pathname = router.state.location.pathname;
  const walletAddress = user?.wallet?.address || "";
  const refCode = sessionStorage.getItem("REF_CODE_USER");

  const signInMutation = useMutation({
    mutationFn: async () => {
      const privyToken = localStorage.getItem("privy:id_token") || "";
      const res = await signinRequest({
        privy_access_token: privyToken.slice(1, -1),
        ref_by: refCode || undefined,
      });
      return res;
    },
    onSuccess: (data) => {
      // Set auth token in cookies
      axiosInstance.defaults.headers.common["Authorization"] =
        `Bearer ${data.access_token}`;
      AXIOS_INSTANCE.defaults.headers.common["Authorization"] =
        `Bearer ${data.access_token}`;
      setCookie("accessToken", data.access_token || "", {
        expires: new Date(new Date().getTime() + 60 * 60 * 1000),
      });
      setCookie(
        "expireAccessToken",
        new Date(new Date().getTime() + 60 * 60 * 1000).toISOString()
      );
      queryClient.refetchQueries({ queryKey: [QUERY_KEY.ME] });
    },
    onError: (error) => {
      console.error("Error during verification:", error);
      showTransactionSnackbar({
        status: "error",
        title: "Something went wrong",
        message: "Please try again later",
      });
      handleLogout();
    },
  });

  useEffect(() => {
    const access_token = getCookie("accessToken");
    if (ready && authenticated && !access_token) {
      signInMutation.mutate();
    }
  }, [ready, authenticated]);

  // Redirect to dashboard if pathname is not defined in config.PATHNAME
  useEffect(() => {
    if (ready && authenticated) {
      const currentPath = pathname;
      const validPaths = Object.values(config.PATHNAME);

      // Check if the current path is not in the list of valid paths
      if (!validPaths.includes(currentPath)) {
        router.navigate({ to: config.PATHNAME.VAULT });
      }
    }
  }, [ready, authenticated, pathname, router]);

  // Redirect effect for unknown pathnames
  useEffect(() => {
    if (ready && !authenticated && !isLoading) {
      const publicPaths = Object.values(config.PATHNAME); // Use paths defined in config
      const isPublicPath = publicPaths.includes(pathname);

      if (!isPublicPath) {
        router.navigate({ to: config.PATHNAME.HOME }); // Redirect to home or any other public page
      }
    }
  }, [ready, authenticated, isLoading, pathname]);

  return (
    <Box>
      <Navigation />
      <main className="bg-[#151517] relative px-4 xl:px-0 border-y-1 border-gray-7">
        <div
          className="absolute top-0 left-0 right-0 z-0"
          style={{
            height: "170px",
            width: "100%",
            background:
              "linear-gradient(180deg, #1F2023 0%, rgba(31, 32, 35, 0) 100%)",
          }}
        ></div>
        <div className="relative max-w-[1224px] mx-auto">
          <DetectNetwork>
            {walletAddress ? (
              // <WhitelistCheckin address={walletAddress}>
                <DetectDifferentAccount>
                  <AutoLogout>{children}</AutoLogout>
                </DetectDifferentAccount>
              // </WhitelistCheckin>
            ) : (
              <AutoLogout>{children}</AutoLogout>
            )}
          </DetectNetwork>
        </div>
        <div
          className="absolute bottom-0 left-0 right-0 z-0"
          style={{
            height: "170px",
            width: "100%",
            background:
              "linear-gradient(0deg, #1F2023 0%, rgba(31, 32, 35, 0) 100%)",
          }}
        ></div>
      </main>

      <Footer />

      {/* Loading Overlay */}
      <Fade in={isLoading}>
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            backdropFilter: "blur(16px)",
          }}
        >
          <SpinnerLoading size={100} />
        </Box>
      </Fade>
    </Box>
  );
}
